import { useCallback } from 'react';

import { useAuthBroadcast } from '@vvoyages/shared-ui/Auth';

import { persistAuthorizationWithProfile } from '@/helpers/api/user-profile';
import { useAuth } from '@/helpers/authUtils';
import { LOGIN_TYPES } from '@/helpers/enumsTypes';
import { useEnvContext } from '@/helpers/envUtils';
import { getRedirectUri, requiresReactivation, signInSuccess } from '@/helpers/signinUtils';
import { useAppRouter } from '@/hooks/useAppRouter';
import { useRedirectUrl } from '@/hooks/useRedirectUrl';

export const useFullPageRedirect = () => {
  const { BASE_URL } = useEnvContext();
  const { redirectUrl } = useRedirectUrl();
  const { postLogin } = useAuthBroadcast();

  return useCallback(
    async (session: any) => {
      // PENDING/DEACTIVE never gets signed in or redirected - it belongs in the reactivation flow
      if (requiresReactivation(session)) return;

      const enrichedSession = await persistAuthorizationWithProfile(BASE_URL, session);

      signInSuccess({ session: enrichedSession, loginType: LOGIN_TYPES.SIGNIN });
      postLogin({
        userId: enrichedSession?.userId,
      });
      const finalRedirectUrl = redirectUrl || getRedirectUri();
      window.location.assign(finalRedirectUrl);
    },
    [BASE_URL, postLogin, redirectUrl],
  );
};

export const useIframeRedirect = () => {
  const { BASE_URL } = useEnvContext();
  const router = useAppRouter();

  return useCallback(
    async (session: any) => {
      // PENDING/DEACTIVE never gets signed in or redirected - it belongs in the reactivation flow
      if (requiresReactivation(session)) return;

      const enrichedSession = await persistAuthorizationWithProfile(BASE_URL, session);

      signInSuccess({ session: enrichedSession, loginType: LOGIN_TYPES.SIGNIN });
      router.push('/iframe/signin-success');
    },
    [BASE_URL, router],
  );
};

export const useIframeAuthCheck = () => {
  const router = useAppRouter();
  const Auth = useAuth();

  return useCallback(async () => {
    const isLoggedIn = await Auth?.isAuth();
    if (!isLoggedIn) return;

    const session = await Auth?.getAuth();
    if (requiresReactivation(session)) return;

    router.push('/iframe/signin-success');
  }, [router, Auth]);
};

export const useFullPageAuthCheck = () => {
  const { redirectUrl } = useRedirectUrl();
  const Auth = useAuth();

  return useCallback(async () => {
    const isLoggedIn = await Auth?.isAuth();
    if (!isLoggedIn) return;

    const session = await Auth?.getAuth();
    if (requiresReactivation(session)) return;

    const finalRedirectUrl = redirectUrl || getRedirectUri();
    window.location.assign(finalRedirectUrl);
  }, [redirectUrl, Auth]);
};
